import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { Sparkles, Loader2, User, Check, AlertTriangle, Plus } from "lucide-react";
import { toast } from "sonner";

export default function AIMatching() {
  const [jobs, setJobs] = useState([]);
  const [selected, setSelected] = useState("");
  const [matches, setMatches] = useState(null);
  const [busy, setBusy] = useState(false);
  const [useAI, setUseAI] = useState(true);
  const [params] = useSearchParams();

  useEffect(() => {
    api.get("/job-orders").then(r => {
      setJobs(r.data);
      const q = params.get("job");
      if (q) setSelected(q);
      else if (r.data.length) setSelected(r.data[0].id);
    });
  }, [params]);

  const run = async () => {
    if (!selected) { toast.error("Select a job order first"); return; }
    setBusy(true); setMatches(null);
    try {
      const { data } = await api.post(`/matching/run?use_ai=${useAI}`, { job_order_id: selected, limit: 5 });
      setMatches(data.matches);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const job = jobs.find(j => j.id === selected);
  const [shortlists, setShortlists] = useState([]);
  const [pickerFor, setPickerFor] = useState(null);
  useEffect(() => { if (selected) api.get("/shortlists", {params:{job_order_id:selected}}).then(r=>setShortlists(r.data)); }, [selected]);
  const addToShortlist = async (candidate_id, shortlistId) => {
    try {
      if (shortlistId === "__new__") {
        const { data } = await api.post("/shortlists", {job_order_id: selected, company_id: job.company_id, name: `Shortlist - ${job.role_title}`, candidate_ids: [candidate_id]});
        toast.success("Shortlist created");
        setShortlists([data, ...shortlists]);
      } else {
        const sl = shortlists.find(s => s.id === shortlistId);
        if (sl.candidate_ids?.includes(candidate_id)) { toast.info("Already on this shortlist"); setPickerFor(null); return; }
        const { data } = await api.patch(`/shortlists/${shortlistId}`, {candidate_ids: [...(sl.candidate_ids || []), candidate_id]});
        setShortlists(shortlists.map(s => s.id === shortlistId ? data : s));
        toast.success("Added to shortlist");
      }
      setPickerFor(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Layout>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-widest text-sky-600 mb-1">AI Matching Engine</div>
          <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight">Candidate-to-job compatibility</h1>
          <p className="text-sm text-slate-500 mt-1">Rule-based scoring + Claude Sonnet 4.6 reasoning. Final decisions stay human.</p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm mb-5">
        <div className="grid md:grid-cols-3 gap-4 items-end">
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-slate-700 mb-1.5">Job order</label>
            <select data-testid="match-job-select" value={selected} onChange={e=>setSelected(e.target.value)} className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm">
              <option value="">Select a job order…</option>
              {jobs.map(j => <option key={j.id} value={j.id}>{j.role_title} — {j.company_name}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <label className="flex items-center gap-2 text-xs text-slate-600 mr-3">
              <input type="checkbox" checked={useAI} onChange={e=>setUseAI(e.target.checked)} data-testid="use-ai-toggle"/>
              Use Claude reasoning
            </label>
            <button data-testid="run-match-btn" onClick={run} disabled={busy || !selected} className="h-10 px-4 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50">
              {busy ? <Loader2 size={14} className="animate-spin"/> : <Sparkles size={14}/>} Run match
            </button>
          </div>
        </div>
        {job && (
          <div className="mt-4 p-3 rounded-md bg-slate-50 border border-slate-200 text-xs text-slate-700">
            <span className="font-semibold text-slate-900">{job.role_title}</span> for <span className="font-semibold">{job.company_name}</span> · {job.industry} · {job.service_model} · Comp ${job.compensation_min?.toFixed(0)}-${job.compensation_max?.toFixed(0)}/mo
          </div>
        )}
      </div>

      {matches && (
        <div className="space-y-4">
          {matches.length === 0 && <div className="text-sm text-slate-500 bg-white border border-slate-200 rounded-xl p-8 text-center">No candidates matched. Try adding candidates first.</div>}
          {matches.map((m, idx) => (
            <div key={m.candidate_id} data-testid={`match-card-${m.candidate_id}`} className={`bg-white border ${m.has_hard_fail ? "border-red-300" : "border-slate-200"} rounded-xl p-5 shadow-sm`}>
              <div className="flex items-start justify-between mb-3 gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-sky-50 text-sky-700 flex items-center justify-center border border-sky-100"><User size={17}/></div>
                  <div>
                    <div className="font-display font-bold text-slate-900">{m.candidate_name}</div>
                    <div className="text-xs text-slate-500">{m.primary_role}{m.seniority ? ` · ${m.seniority}` : ""} · {m.country} · Score ★ {m.staffhub_score?.toFixed(1)}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Match</div>
                  <div className={`font-display text-3xl font-bold ${m.has_hard_fail?"text-red-600":m.match_score>=80?"text-emerald-600":m.match_score>=60?"text-amber-600":"text-slate-500"}`}>{m.match_score}%</div>
                  {m.has_hard_fail && <div className="text-[9px] uppercase tracking-widest text-red-600 font-semibold mt-0.5">Hard-req fail</div>}
                </div>
              </div>
              {m.has_hard_fail && (
                <div className="mb-3 p-3 rounded-md bg-red-50 border border-red-200">
                  <div className="text-[11px] font-semibold text-red-800 uppercase tracking-widest mb-1.5 flex items-center gap-1"><AlertTriangle size={12}/> Hard requirement failures</div>
                  <ul className="text-xs text-red-900 space-y-0.5">{m.hard_fails.map((s,i)=><li key={i}>· {s}</li>)}</ul>
                </div>
              )}
              <div className="grid md:grid-cols-2 gap-3 mt-3">
                <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3">
                  <div className="text-[11px] font-semibold text-emerald-800 uppercase tracking-widest mb-2 flex items-center gap-1"><Check size={12}/> Why this fits</div>
                  <ul className="text-xs text-emerald-900 space-y-1">{(m.strengths||[]).map((s,i)=><li key={i}>· {s}</li>)}</ul>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                  <div className="text-[11px] font-semibold text-amber-800 uppercase tracking-widest mb-2 flex items-center gap-1"><AlertTriangle size={12}/> Concerns</div>
                  <ul className="text-xs text-amber-900 space-y-1">{(m.concerns||[]).length === 0 ? <li>· None flagged</li> : m.concerns.map((s,i)=><li key={i}>· {s}</li>)}</ul>
                </div>
              </div>
              {m.ai_reasoning && (
                <div className="mt-3 p-3 rounded-md bg-sky-50 border border-sky-200">
                  <div className="text-[11px] font-semibold text-sky-800 uppercase tracking-widest mb-1.5 flex items-center gap-1"><Sparkles size={12}/> Claude reasoning</div>
                  <div className="text-xs text-sky-900">{m.ai_reasoning}</div>
                  {m.interview_questions?.length > 0 && (
                    <div className="mt-2">
                      <div className="text-[10px] uppercase tracking-widest font-semibold text-sky-800 mb-1">Suggested interview questions</div>
                      <ul className="text-xs text-sky-900 space-y-0.5">{m.interview_questions.map((q,i)=><li key={i}>{i+1}. {q}</li>)}</ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Layout>
  );
}
